import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Save,
  Delete,
  Schedule,
  Archive,
  PlayArrow,
} from '@mui/icons-material';
import api from '../../services/api';

interface RetentionSettings {
  activity_retention_days: number;
  activity_retention_by_type: { [key: string]: number };
  activity_retention_by_level: { [key: string]: number };
  enable_automatic_cleanup: boolean;
  cleanup_schedule: string;
  minimum_retention_days: number;
  archive_before_delete: boolean;
  retention_exclude_users: string[];
  retention_exclude_clubs: string[];
}

interface RetentionStats {
  total_activities: number;
  activities_by_age: { [key: string]: number };
  activities_to_delete: number;
  excluded_activities: {
    by_user: number;
    by_club: number;
    total: number;
  };
  retention_settings: {
    default_retention_days: number;
    automatic_cleanup_enabled: boolean;
    archive_before_delete: boolean;
  };
}

const RetentionSettingsComponent: React.FC = () => {
  const [settings, setSettings] = useState<RetentionSettings | null>(null);
  const [stats, setStats] = useState<RetentionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [previewResults, setPreviewResults] = useState<any>(null);

  useEffect(() => {
    fetchSettings();
    fetchStatistics();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/retention/settings');
      setSettings(response.data);
    } catch (err: any) {
      setError('Failed to load retention settings');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/retention/statistics');
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      await api.put('/retention/settings', settings);
      setSuccess('Retention settings updated successfully');
      fetchStatistics(); // Refresh statistics
    } catch (err: any) {
      setError('Failed to save retention settings');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewRetention = async () => {
    try {
      const response = await api.post('/retention/apply?dry_run=true');
      setPreviewResults(response.data);
      setApplyDialogOpen(true);
    } catch (err: any) {
      setError('Failed to preview retention policy');
      console.error('Error previewing retention:', err);
    }
  };

  const handleApplyRetention = async () => {
    try {
      const response = await api.post('/retention/apply?dry_run=false');
      setSuccess(`Successfully deleted ${response.data.deleted_count} activities`);
      setApplyDialogOpen(false);
      fetchStatistics(); // Refresh statistics
    } catch (err: any) {
      setError('Failed to apply retention policy');
      console.error('Error applying retention:', err);
    }
  };

  const updateActivityTypeRetention = (type: string, days: number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      activity_retention_by_type: {
        ...settings.activity_retention_by_type,
        [type]: days,
      },
    });
  };

  const updateActivityLevelRetention = (level: string, days: number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      activity_retention_by_level: {
        ...settings.activity_retention_by_level,
        [level]: days,
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">Failed to load retention settings</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Archive />
        Activity Retention Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h4">
                  {stats.total_activities.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  To Be Deleted
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.activities_to_delete.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Excluded Activities
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.excluded_activities.total.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Cleanup Status
                </Typography>
                <Chip
                  label={settings.enable_automatic_cleanup ? 'Enabled' : 'Disabled'}
                  color={settings.enable_automatic_cleanup ? 'success' : 'default'}
                  size="small"
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* General Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Retention Settings
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <TextField
              fullWidth
              label="Default Retention Days"
              type="number"
              value={settings.activity_retention_days}
              onChange={(e) => setSettings({
                ...settings,
                activity_retention_days: parseInt(e.target.value) || 90,
              })}
              InputProps={{ inputProps: { min: 7, max: 3650 } }}
              helperText="Number of days to keep activities by default"
            />
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <TextField
              fullWidth
              label="Minimum Retention Days"
              type="number"
              value={settings.minimum_retention_days}
              onChange={(e) => setSettings({
                ...settings,
                minimum_retention_days: parseInt(e.target.value) || 7,
              })}
              InputProps={{ inputProps: { min: 1, max: 365 } }}
              helperText="Minimum days to keep any activity"
            />
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <TextField
              fullWidth
              label="Cleanup Schedule (Cron)"
              value={settings.cleanup_schedule}
              onChange={(e) => setSettings({
                ...settings,
                cleanup_schedule: e.target.value,
              })}
              helperText="Cron expression for automatic cleanup"
              InputProps={{
                startAdornment: <Schedule sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable_automatic_cleanup}
                    onChange={(e) => setSettings({
                      ...settings,
                      enable_automatic_cleanup: e.target.checked,
                    })}
                  />
                }
                label="Enable Automatic Cleanup"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.archive_before_delete}
                    onChange={(e) => setSettings({
                      ...settings,
                      archive_before_delete: e.target.checked,
                    })}
                  />
                }
                label="Archive Before Delete"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Retention by Activity Type */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Retention by Activity Type
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {Object.entries(settings.activity_retention_by_type).map(([type, days]) => (
            <Box key={type} sx={{ flex: '1 1 30%', minWidth: '200px' }}>
              <TextField
                fullWidth
                label={type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                type="number"
                value={days}
                onChange={(e) => updateActivityTypeRetention(type, parseInt(e.target.value) || 90)}
                InputProps={{ inputProps: { min: 1, max: 3650 } }}
                size="small"
              />
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Retention by Activity Level */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Retention by Activity Level
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {Object.entries(settings.activity_retention_by_level).map(([level, days]) => (
            <Box key={level} sx={{ flex: '1 1 22%', minWidth: '150px' }}>
              <TextField
                fullWidth
                label={level.charAt(0).toUpperCase() + level.slice(1)}
                type="number"
                value={days}
                onChange={(e) => updateActivityLevelRetention(level, parseInt(e.target.value) || 90)}
                InputProps={{ inputProps: { min: 1, max: 3650 } }}
                size="small"
              />
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<PlayArrow />}
          onClick={handlePreviewRetention}
        >
          Preview Retention
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {/* Apply Retention Dialog */}
      <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Retention Policy Preview</DialogTitle>
        <DialogContent>
          {previewResults && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will permanently delete {previewResults.deleted_count} activities.
                {settings.archive_before_delete && ' Activities will be archived before deletion.'}
              </Alert>

              <Typography variant="subtitle1" gutterBottom>
                Activities to be deleted: {previewResults.deleted_count}
              </Typography>

              {previewResults.activities_to_delete && previewResults.activities_to_delete.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview (first 10 activities):
                  </Typography>
                  <List dense>
                    {previewResults.activities_to_delete.slice(0, 10).map((activity: any) => (
                      <ListItem key={activity.id}>
                        <ListItemText
                          primary={activity.title}
                          secondary={`${activity.type} - ${new Date(activity.timestamp).toLocaleDateString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApplyRetention}
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            Apply Retention Policy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RetentionSettingsComponent;